/**
 * Keycloak client-group parsing, mirroring the backend's
 * `app/client_access.py`. Both sides must agree or the UI will show data
 * the API then refuses to serve.
 *
 * All environments share the `provisioning-anep` realm, so what scopes a
 * user to one environment is their group membership under /Clients/{uuid}:
 *
 *   /Clients/{uuid}/{env}     grants access on that environment
 *   /Clients/{uuid}/members   roster marker — grants nothing, but records
 *                             that the user was migrated off the flat group
 *   /Clients/{uuid}           legacy flat group, granting every environment
 *                             and honoured only while the user has no
 *                             subgroup at all under that client
 *
 * The "subgroups win over the flat group" rule is what makes a revoke stick:
 * without it, removing a user's last environment would fall back to the flat
 * group and silently restore access everywhere.
 */

export const CLIENT_GROUP_PREFIX = '/Clients/';

/**
 * Membership marks a user as ANEP platform staff rather than a customer's
 * technician. Mirrors ANEP_GROUP in the backend's client_access.py.
 *
 * It is what makes the two spaces distinguishable: `Viewer` exists on both
 * sides and the role name alone cannot say which. A Viewer inside
 * /Clients/{uuid}/{env} reads one organisation; a Viewer inside /Anep reads
 * the whole platform.
 */
export const ANEP_GROUP = '/Anep';
export const ROSTER_SUBGROUP = 'members';

export interface ParsedClientGroup {
  clientId: string;
  /** Environment name, the roster marker, or null for the flat group. */
  subgroup: string | null;
}

/**
 * Split a Keycloak group path into its client UUID and subgroup.
 * Returns null for paths outside the /Clients/ tree or malformed ones.
 */
export const parseClientGroup = (groupPath: string): ParsedClientGroup | null => {
  if (typeof groupPath !== 'string' || !groupPath.startsWith(CLIENT_GROUP_PREFIX)) {
    return null;
  }

  const remainder = groupPath.slice(CLIENT_GROUP_PREFIX.length).replace(/^\/+|\/+$/g, '');
  if (!remainder) return null;

  const parts = remainder.split('/');
  if (parts.length === 1) return { clientId: parts[0], subgroup: null };
  if (parts.length === 2) return { clientId: parts[0], subgroup: parts[1].toLowerCase() };
  return null;
};

/**
 * Every client UUID appearing in a set of group paths, regardless of
 * environment. Use this for display ("which clients is this user attached
 * to?"), never for deciding what the user may reach.
 */
export const clientIdsFromGroupPaths = (groupPaths: string[]): string[] => {
  const seen: string[] = [];
  for (const path of groupPaths || []) {
    const parsed = parseClientGroup(path);
    if (parsed && !seen.includes(parsed.clientId)) {
      seen.push(parsed.clientId);
    }
  }
  return seen;
};

/**
 * Client UUIDs a set of group paths actually grants on `env`.
 *
 * Mirrors `client_ids_from_groups` in the backend: subgroups are
 * authoritative once any exists for a client, otherwise the legacy flat
 * group grants every environment.
 */
export const clientIdsFromGroups = (groupPaths: string[], env: string): string[] => {
  const target = (env || '').toLowerCase();
  const order: string[] = [];
  const flat = new Set<string>();
  const subgroups = new Map<string, Set<string>>();

  for (const path of groupPaths || []) {
    const parsed = parseClientGroup(path);
    if (!parsed) continue;

    if (!order.includes(parsed.clientId)) order.push(parsed.clientId);

    if (parsed.subgroup === null) {
      flat.add(parsed.clientId);
    } else {
      const set = subgroups.get(parsed.clientId) ?? new Set<string>();
      set.add(parsed.subgroup);
      subgroups.set(parsed.clientId, set);
    }
  }

  return order.filter((clientId) => {
    const subs = subgroups.get(clientId);
    // Migrated user: environment subgroups are authoritative.
    if (subs && subs.size > 0) return subs.has(target);
    // Legacy user: the flat group still grants every environment.
    return flat.has(clientId);
  });
};

/**
 * Derive the environment name from the OAuth client id, matching the
 * backend's fallback ("provisioning-staging" -> "staging"). The backend is
 * the authority on its own env; this is only used for client-side filtering
 * and display, so a mismatch degrades the UI rather than access control.
 */
export const environmentFromOAuthClientId = (oauthClientId: string): string => {
  if (!oauthClientId || !oauthClientId.includes('-')) return 'dev';
  return oauthClientId.slice(oauthClientId.lastIndexOf('-') + 1).toLowerCase();
};


/**
 * Whether these group paths mark ANEP platform staff **on `env`**.
 *
 * Mirrors `is_anep_staff` in the backend, and follows the same rule as client
 * membership for the same reason: the three environments share one realm, so
 * a realm-wide group would hand someone dev, staging and prod at once.
 *
 *   /Anep/{env}   grants that environment only
 *   /Anep         legacy flat membership — grants every environment, but only
 *                 while the member has no /Anep/* subgroup at all
 *
 * ANEP staff are attached to no client, so they resolve to an empty client
 * scope. Without this check they would look identical to a revoked account
 * and be locked out of the application.
 */
export const isAnepStaffFromGroups = (groupPaths: string[], env: string): boolean => {
  const target = (env || '').toLowerCase();
  let flat = false;
  const envs = new Set<string>();

  for (const g of groupPaths || []) {
    if (typeof g !== 'string') continue;
    if (g === ANEP_GROUP) {
      flat = true;
    } else if (g.startsWith(`${ANEP_GROUP}/`)) {
      const remainder = g.slice(ANEP_GROUP.length + 1).replace(/^\/+|\/+$/g, '');
      if (remainder && !remainder.includes('/')) envs.add(remainder.toLowerCase());
    }
  }

  // Scoped member: the subgroups are authoritative.
  if (envs.size > 0) return envs.has(target);
  return flat;
};
